import { useState, useRef, useCallback, useEffect } from 'react';

export function useSwipe(ref, { threshold = 80, onSwipeLeft } = {}) {
  const [deltaX, setDeltaX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isScrolling = useRef(null);
  const startTime = useRef(0);
  const deltaRef = useRef(0);
  const onSwipeLeftRef = useRef(onSwipeLeft);
  onSwipeLeftRef.current = onSwipeLeft;

  const reset = useCallback(() => {
    setDeltaX(0);
    deltaRef.current = 0;
    setSwiping(false);
    isScrolling.current = null;
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function onTouchStart(e) {
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      startTime.current = Date.now();
      isScrolling.current = null;
      setSwiping(true);
    }

    function onTouchMove(e) {
      const touch = e.touches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      if (isScrolling.current === null) {
        isScrolling.current = Math.abs(dy) > Math.abs(dx);
        if (isScrolling.current) {
          setSwiping(false);
          return;
        }
      }

      if (isScrolling.current) return;

      const clamped = Math.min(0, Math.max(dx, -160));
      deltaRef.current = clamped;
      setDeltaX(clamped);
    }

    function onTouchEnd() {
      if (isScrolling.current) {
        setDeltaX(0);
        deltaRef.current = 0;
        setSwiping(false);
        isScrolling.current = null;
        return;
      }

      const dx = deltaRef.current;
      const elapsed = Date.now() - startTime.current;
      const velocity = Math.abs(dx) / elapsed;
      const triggered = Math.abs(dx) >= threshold || (velocity > 0.5 && Math.abs(dx) > 30);

      if (triggered && dx < 0 && onSwipeLeftRef.current) {
        setDeltaX(-threshold);
        deltaRef.current = -threshold;
        onSwipeLeftRef.current();
      } else {
        setDeltaX(0);
        deltaRef.current = 0;
      }
      setSwiping(false);
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [ref, threshold]);

  return { deltaX, swiping, reset };
}
